"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  Panel,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { downloadText } from "@/lib/tool-kit/download";
import {
  type AlphabetId,
  type CheckKind,
  generateIds,
  type IdOptions,
  type IdProblem,
  idsToCsv,
  MAX_IDS,
  MAX_LENGTH,
  type Mode,
  spaceSize,
  type Verdict,
  validateId,
} from "@/lib/tool-kit/research-ops/participant-id";

/** English strings. A translated content file overrides these through `ui`. */
export const PARTICIPANT_ID_GENERATOR_STRINGS = {
  modeLegend: "Kind of code",
  modeRandom: "Random",
  modeSequential: "Sequential",
  modeRandomNote:
    "Each code is drawn at random from your browser's cryptographic random generator, so it says nothing about who enrolled first.",
  modeSequentialNote:
    "Numbers in order. Easy to file, but a sequential ID reveals enrollment order and how many people took part, and anyone can guess the next one.",
  countLabel: "How many IDs",
  countHint: "Up to {max}.",
  lengthLabel: "Length of the code (before the check character)",
  lengthHint: "1 to {max} symbols.",
  startLabel: "First number",
  alphabetLegend: "Symbols",
  alphabetDigits: "Digits 0–9",
  alphabetBase32: "Digits and letters, no look-alikes",
  alphabetNote:
    "The letter option uses Crockford's Base32 alphabet — no I, L, O or U — so a typed ID is never confused with 1, 0 or V.",
  checkLegend: "Check character",
  checkNone: "None",
  checkLuhn: "Luhn",
  checkDamm: "Damm (digits only)",
  checkNotes: {
    none: "No check character. Simplest, but a mistyped ID may match a real one.",
    luhn: "Luhn catches every single-symbol typing error and most swaps of neighbouring symbols — but not 09 ↔ 90 in digits. It works over either symbol set.",
    damm: "Damm catches every single-digit error and every swap of two neighbouring digits. It needs digits.",
  },
  checkNever:
    "A check character catches typing mistakes. It is not a secret and does not protect anything: anyone can compute it.",
  prefixLabel: "Prefix (optional)",
  prefixPlaceholder: "e.g. P or STUDY1",
  prefixHint: "Use a study code, never initials or anything about a person.",
  hyphenLabel: "Put a hyphen after the prefix",
  groupLabel: "Split the code with hyphens every",
  groupNone: "Do not split",
  groupOption: "{n} symbols",
  columnsLabel: "Blank columns to add to the CSV (optional, comma-separated)",
  columnsPlaceholder: "e.g. group, date enrolled",
  columnsHint:
    "The key file is where you later record group and date. Keep anything that could identify a person in a separate, protected file.",
  idHeader: "participant_id",
  generate: "Generate IDs",
  regenerate: "Generate a new set",
  resultsHeading: "Participant IDs",
  prompt: "Choose your options and press Generate IDs.",
  problems: {
    count: "Ask for a whole number of IDs from 1 to {max}.",
    length: "The code length must be a whole number from 1 to {max}.",
    "too-many":
      "That length allows only {space} different codes — fewer than you asked for. Lengthen the code or ask for fewer IDs.",
    "damm-needs-digits":
      "Damm works on digits only. Choose digits, or use Luhn for letters and digits.",
    start: "The first number must be a whole number, zero or more.",
    "sequential-overflow":
      "Sequential codes use digits and must fit in the length you chose. Lengthen the code or lower the count.",
  },
  summary:
    "{count} IDs generated. {space} codes are possible with these settings, so this list uses {share} of them.",
  summarySequential: "{count} sequential IDs generated.",
  colNumber: "#",
  colId: "ID",
  shownRows:
    "Showing the first {shown} of {total}; the copy and the CSV have them all.",
  copyList: "Copy the list",
  copied: "Copied!",
  downloadCsv: "Download CSV",
  csvFile: "participant-ids.csv",
  checkerHeading: "Check an ID",
  checkerLabel: "Type or paste an ID",
  checkerHint:
    "Checks it against the prefix, symbols and check character set above. It cannot tell you whether the ID was ever issued.",
  verdicts: {
    valid: "Valid: the check character matches.",
    invalid:
      "Not valid: the check character does not match. Look for a mistyped symbol.",
    unchecked: "No check character is selected, so there is nothing to verify.",
    foreign:
      "Not one of these IDs: the prefix or the symbols do not fit the settings above.",
  },
  safetyHeading: "Keep the codes non-identifying",
  safety:
    "A participant ID should say nothing about the person: no initials, birth date, phone number, student number or email. Store the file linking IDs to identities separately, with restricted access, and delete it when your ethics approval says to. These codes are not seeded and cannot be recreated, so save the list when you generate it.",
} as const;

const GROUPS = [0, 3, 4, 5] as const;
const SHOWN_ROWS = 200;
const COMMA = /,/;

function problemText(
  problem: IdProblem,
  strings: typeof PARTICIPANT_ID_GENERATOR_STRINGS,
  options: IdOptions,
  fmt: (value: number) => string
): string {
  return t(strings.problems[problem], {
    max: fmt(problem === "length" ? MAX_LENGTH : MAX_IDS),
    space: fmt(spaceSize(options)),
  });
}

export default function ParticipantIdGeneratorWidget() {
  const strings = useToolUi(PARTICIPANT_ID_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const [countText, setCountText] = useState("50");
  const [lengthText, setLengthText] = useState("6");
  const [startText, setStartText] = useState("1");
  const [mode, setMode] = useState<Mode>("random");
  const [alphabet, setAlphabet] = useState<AlphabetId>("digits");
  const [check, setCheck] = useState<CheckKind>("luhn");
  const [prefix, setPrefix] = useState("P");
  const [hyphen, setHyphen] = useState(true);
  const [group, setGroup] = useState("3");
  const [columns, setColumns] = useState("");
  const [ids, setIds] = useState<string[] | null>(null);
  const [problem, setProblem] = useState<IdProblem | null>(null);
  const [candidate, setCandidate] = useState("");

  const options: IdOptions = {
    count: parseIntegerInput(countText) ?? 0,
    mode,
    length: parseIntegerInput(lengthText) ?? 0,
    alphabet: mode === "sequential" ? "digits" : alphabet,
    check,
    prefix: prefix.trim(),
    hyphen,
    group: Number(group),
    start: parseIntegerInput(startText) ?? -1,
  };

  function generate() {
    const result = generateIds(options, (words) =>
      globalThis.crypto.getRandomValues(words)
    );
    if (result.ok) {
      setIds(result.ids);
      setProblem(null);
    } else {
      setIds(null);
      setProblem(result.problem);
    }
  }

  const extra = columns
    .split(COMMA)
    .map((name) => name.trim())
    .filter((name) => name !== "");
  const verdict: Verdict | null =
    candidate.trim() === "" ? null : validateId(candidate, options);
  const share = ids && mode === "random" ? ids.length / spaceSize(options) : 0;

  return (
    <div className="space-y-8">
      <Panel>
        <PillGroup
          label={strings.modeLegend}
          name="pid-mode"
          onChange={setMode}
          options={[
            { value: "random", label: strings.modeRandom },
            { value: "sequential", label: strings.modeSequential },
          ]}
          value={mode}
        />
        <p className={HINT}>
          {mode === "random"
            ? strings.modeRandomNote
            : strings.modeSequentialNote}
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            hint={t(strings.countHint, { max: fmt(MAX_IDS) })}
            id="pid-count"
            label={strings.countLabel}
            numeric
            onChange={setCountText}
            value={countText}
          />
          <TextField
            hint={t(strings.lengthHint, { max: fmt(MAX_LENGTH) })}
            id="pid-length"
            label={strings.lengthLabel}
            numeric
            onChange={setLengthText}
            value={lengthText}
          />
          {mode === "sequential" ? (
            <TextField
              id="pid-start"
              label={strings.startLabel}
              numeric
              onChange={setStartText}
              value={startText}
            />
          ) : null}
        </div>
        {mode === "random" ? (
          <>
            <PillGroup
              label={strings.alphabetLegend}
              name="pid-alphabet"
              onChange={setAlphabet}
              options={[
                { value: "digits", label: strings.alphabetDigits },
                { value: "base32", label: strings.alphabetBase32 },
              ]}
              value={alphabet}
            />
            <p className={HINT}>{strings.alphabetNote}</p>
          </>
        ) : null}
        <PillGroup
          label={strings.checkLegend}
          name="pid-check"
          onChange={setCheck}
          options={[
            { value: "none", label: strings.checkNone },
            { value: "luhn", label: strings.checkLuhn },
            { value: "damm", label: strings.checkDamm },
          ]}
          value={check}
        />
        <p className={HINT}>
          {strings.checkNotes[check]} {strings.checkNever}
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            hint={strings.prefixHint}
            id="pid-prefix"
            label={strings.prefixLabel}
            onChange={setPrefix}
            placeholder={strings.prefixPlaceholder}
            value={prefix}
          />
          <SelectField
            id="pid-group"
            label={strings.groupLabel}
            onChange={setGroup}
            options={GROUPS.map((n) => ({
              value: String(n),
              label:
                n === 0 ? strings.groupNone : t(strings.groupOption, { n }),
            }))}
            value={group}
          />
        </div>
        <Checkbox
          checked={hyphen}
          label={strings.hyphenLabel}
          onChange={setHyphen}
        />
        <TextField
          hint={strings.columnsHint}
          id="pid-columns"
          label={strings.columnsLabel}
          onChange={setColumns}
          placeholder={strings.columnsPlaceholder}
          value={columns}
        />
        <button className={BUTTON_PRIMARY} onClick={generate} type="button">
          {ids ? strings.regenerate : strings.generate}
        </button>
      </Panel>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {problem ? (
          <ErrorNotice>
            {problemText(problem, strings, options, fmt)}
          </ErrorNotice>
        ) : null}
        {ids ? (
          <div className="space-y-4">
            <p className="text-foreground text-sm">
              {mode === "random"
                ? t(strings.summary, {
                    count: fmt(ids.length),
                    space: fmt(spaceSize(options)),
                    share: fmt(share, {
                      style: "percent",
                      maximumSignificantDigits: 2,
                    }),
                  })
                : t(strings.summarySequential, { count: fmt(ids.length) })}
            </p>
            <div className="flex flex-wrap gap-3">
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyList}
                text={ids.join("\n")}
              />
              <button
                className={BUTTON_SECONDARY}
                onClick={() =>
                  downloadText(
                    strings.csvFile,
                    idsToCsv(ids, strings.idHeader, extra),
                    "text/csv;charset=utf-8"
                  )
                }
                type="button"
              >
                {strings.downloadCsv}
              </button>
            </div>
            <DataTable
              head={[strings.colNumber, strings.colId]}
              rows={ids
                .slice(0, SHOWN_ROWS)
                .map((id, index) => [fmt(index + 1), id])}
            />
            {ids.length > SHOWN_ROWS ? (
              <p className={HINT}>
                {t(strings.shownRows, {
                  shown: fmt(SHOWN_ROWS),
                  total: fmt(ids.length),
                })}
              </p>
            ) : null}
          </div>
        ) : (
          <Notice>{strings.prompt}</Notice>
        )}
      </section>

      <section className={`${RESULT_CARD} space-y-3`}>
        <h3 className={WIDGET_H3}>{strings.checkerHeading}</h3>
        <TextField
          hint={strings.checkerHint}
          id="pid-checker"
          label={strings.checkerLabel}
          mono
          onChange={setCandidate}
          value={candidate}
        />
        <p aria-live="polite" className="text-foreground text-sm">
          {verdict ? strings.verdicts[verdict] : null}
        </p>
      </section>

      <section className="space-y-1">
        <h3 className={WIDGET_H3}>{strings.safetyHeading}</h3>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.safety}
        </p>
      </section>
    </div>
  );
}
